// ----------------------------------------------------------------------
// <copyright file="Izenda.WorkspaceMapping.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  const { uniqueId, map, split, toLower, find } = require('lodash/fp');

  var m = require('mobx');
  var constants = require('logic/data/Izenda.CopyManagementConstants');
  var trackable = require('share/tracking/Izenda.Trackable');
  var WorkspaceMappingTenant = require('./Izenda.WorkspaceMappingTenant');

  function WorkspaceMapping(data, databaseSources, sourceConnections) {
    var props = {
      uniqueId: uniqueId,
      // State props
      workspaceId: null,
      fromDatabaseName: '',
      type: constants.MAPPING_TYPE.Schema,
      fromObject: '',
      fromServer: '',
      fromDatabaseUser: '',
      toDatabaseName: '',
      toObject: '',
      toServer: '',
      toDatabaseUser: '',
      isGlobal: false,
      id: null,
      inserted: false,
      version: null,
      created: null,
      createdBy: null,
      modified: null,
      modifiedBy: null,
      workspaceMappingTenants: [],
      fromDatabaseKey: function(){
        return this.fromDatabaseName + ' - ' + this.fromServer + ' - ' + this.fromDatabaseUser;
      },
      toDatabaseKey: function(){
        return this.toDatabaseName + ' - ' + this.toServer + ' - ' + this.toDatabaseUser;
      },
      mappingTenantIds: function() {
        return map(function(tenant) {
          return tenant.tenantId;
        }, this.workspaceMappingTenants).toString();
      }
    };

    m.extendObservable(trackable(this), props);

    data && this.readWorkspaceMapping(data, databaseSources, sourceConnections);
  }

  /**
   * Read workspace mapping object
   * @param {Object} data
   */
  WorkspaceMapping.prototype.readWorkspaceMapping = m.action(function(data, databaseSources, sourceConnections) {
    this.workspaceId = data.workspaceId || null;
    this.fromDatabaseName = data.fromDatabaseName || '';
    this.type = data.type || constants.MAPPING_TYPE.Schema;
    this.fromObject = this.getFromObject(data, databaseSources);
    this.fromServer = data.fromServer;
    this.fromDatabaseUser = data.fromDatabaseUser,
    this.toDatabaseName = data.toDatabaseName || '';
    this.toObject = this.getToObject(data, sourceConnections);
    this.toServer = data.toServer;
    this.toDatabaseUser = data.toDatabaseUser,
    this.isGlobal = data.isGlobal || false;
    this.id = data.id || null;
    this.inserted = data.inserted || false;
    this.version = data.version || null;
    this.created = data.created || null;
    this.createdBy = data.createdBy || null;
    this.modified = data.modified || null;
    this.modifiedBy = data.modifiedBy || null;
    this.workspaceMappingTenants = map(function(tenant) {
      return new WorkspaceMappingTenant(tenant);
    }, data.workspaceMappingTenants);
  });

  /**
   * Logic get from data object
   * @param {type} data
   */
  WorkspaceMapping.prototype.getFromObject = function(data, databaseSources) {
    if (!data.fromObject && this.type === constants.MAPPING_TYPE.Database) {
      var selectedDB = find({ fullDBName: data.fromDatabaseName, databaseServer: data.fromServer, databaseUser: data.fromDatabaseUser }, databaseSources);
      return selectedDB ? selectedDB.name : '';
    }

    return data.fromObject || '';
  };

  /**
   * Logic get to data object
   * @param {type} data
   */
  WorkspaceMapping.prototype.getToObject = function(data, databaseSources) {
    if (!data.toObject && this.type === constants.MAPPING_TYPE.Database) {
      var selectedDB = find({ fullDBName: data.toDatabaseName, databaseServer: data.toServer, databaseUser: data.toDatabaseUser }, databaseSources);
      return selectedDB ? selectedDB.name : '';
    }

    return data.toObject || '';
  };

  /**
   * Update Mapping Tenant
   */
  WorkspaceMapping.prototype.updateMappingTenant = m.action(function(tenants) {
    var listSelectedTenant = split(',', tenants);
    var workspaceId = this.workspaceId;

    this.workspaceMappingTenants = map(function(tenantId) {
      return new WorkspaceMappingTenant({ tenantId: tenantId, workspaceId: workspaceId });
    }, listSelectedTenant);
  });

  /**
   * Check database is in options list or not
   * If not exist , set the default = first value
   */
  WorkspaceMapping.prototype.checkDatabaseName = function(databaseNames, key) {
    var selectedDatabase = find({ fullDBName: this[key] }, databaseNames);
    if (!selectedDatabase) {
      this[key] = databaseNames[0].fullDBName;
    }
  };

  /**
   * Check data object is in options list or not
   * If not exist , set the default = first value
   */
  WorkspaceMapping.prototype.checkDataObject = function(dataObjects) {
    var selectedDataObject = find({ name: this.fromObject }, dataObjects);
    if (!selectedDataObject) {
      this.fromObject = dataObjects[0].name;
    }
  };

  /**
   * Compare 2 mapping item
   * @param {type} mappingItem
   */
  WorkspaceMapping.prototype.compare = function(mappingItem, selectedTenant) {
    return (
      this.workspaceId === mappingItem.workspaceId &&
      this.fromDatabaseName === mappingItem.fromDatabaseName &&
      this.type === mappingItem.type &&
      this.fromObject === mappingItem.fromObject &&
      (toLower(this.toObject) === toLower(mappingItem.toObject) ||
        (toLower(this.toObject).indexOf(constants.TENANT_TOKEN) > -1 &&
          toLower(mappingItem.toObject).replace(selectedTenant.tenantUniqueName, constants.TENANT_TOKEN) ===
            toLower(this.toObject)) ||
        (toLower(this.toObject).indexOf(constants.TENANT_NAME_TOKEN) > -1 &&
          toLower(mappingItem.toObject).replace(selectedTenant.name, constants.TENANT_NAME_TOKEN) ===
            toLower(this.toObject)))
    );
  };

  /**
   * Add mapping tenant
   * @param {type} mappingItem
   */
  WorkspaceMapping.prototype.addMappingTenant = function(tenantId) {
    var isExistTenant = find({ tenantId: tenantId }, this.workspaceMappingTenants);
    !isExistTenant && this.workspaceMappingTenants.push({ workspaceMappingId: this.id, tenantId: tenantId });
  };

  module.exports = WorkspaceMapping;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.WorkspaceMapping.js